/**
 * Dedicated sign-in page (route `/login`, anonymous only). The landing's single "Sign in" / "Get started"
 * CTA lands here instead of stacking provider buttons on the landing itself. Pick a provider (Google /
 * GitHub — Microsoft is added when it's configured); the choice is remembered and surfaced FIRST next time
 * with a "Last used" hint. The button both signs in an existing user and creates an account
 * (provisionFromOAuth), hence "Continue with X".
 */

/** Providers on the chooser. `key` matches `/auth/<key>/login` + the last-used store. `brand` = Google's
 *  own button styling. Add `{ key: "microsoft", label: "Microsoft" }` when Microsoft login is enabled. */
const PROVIDERS: { key: string; label: string; brand?: boolean }[] = [
  { key: "google", label: "Google", brand: true },
  { key: "github", label: "GitHub" },
];

const LAST_KEY = "amendie_last_provider";

function readLast(): string | null {
  try {
    const v = localStorage.getItem(LAST_KEY);
    return PROVIDERS.some((p) => p.key === v) ? v : null;
  } catch {
    return null;
  }
}

export function Login() {
  const last = readLast();
  // Last-used provider first, so the returning user's obvious click sits at the top.
  const ordered = last
    ? [...PROVIDERS].sort((a, b) => (a.key === last ? -1 : b.key === last ? 1 : 0))
    : PROVIDERS;

  const go = (key: string): void => {
    try {
      localStorage.setItem(LAST_KEY, key);
    } catch {
      /* private-mode / storage disabled — proceed without remembering */
    }
    // Full navigation to the top-level OAuth entry (not an /api route).
    window.location.href = `/auth/${key}/login`;
  };

  return (
    <div className="gate">
      <div className="gate-card login-card">
        <div className="brand big">Amendie</div>
        <h2 className="gate-title">Sign in</h2>
        <p className="muted">Continue with your account — we sign you in, or create one if you&rsquo;re new.</p>
        <div className="login-providers">
          {ordered.map((p) => (
            <button
              key={p.key}
              // Last-used provider carries no label — it just sits first (reorder above) and wears a
              // gold ring (.is-last). The badge text was noise; position + ring say "this one" already.
              className={`${p.brand ? "btn google lg" : "btn ghost lg"}${p.key === last ? " is-last" : ""}`}
              onClick={() => go(p.key)}
            >
              <span>Continue with {p.label}</span>
            </button>
          ))}
        </div>
        <a className="link-btn login-back" href="/">
          &larr; Back to home
        </a>
      </div>
    </div>
  );
}
